/**
 * 用量趋势折线图（按天）：tokens 或 费用。
 *
 * @module components/charts/UsageLineChart
 */
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import Box from '@mui/material/Box';
import type { UsageDailyPoint } from '@/types';
import { formatMoney, formatNumber } from '@/utils/format';

/** UsageLineChart 属性。 */
export interface UsageLineChartProps {
  /** 按日用量数据。 */
  data: UsageDailyPoint[];
  /** 指标：tokens 或 cost。 */
  metric?: 'tokens' | 'cost';
  /** 图表高度。 */
  height?: number;
}

/**
 * 用量趋势折线图。
 *
 * @param props 组件属性。
 * @returns JSX 元素。
 */
export default function UsageLineChart({
  data,
  metric = 'tokens',
  height = 280,
}: UsageLineChartProps): JSX.Element {
  const isCost = metric === 'cost';
  const dataKey = isCost ? 'cost' : 'tokens';

  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
          <YAxis
            tick={{ fontSize: 12 }}
            width={56}
            tickFormatter={(v: number): string =>
              isCost ? formatMoney(v, 0) : formatNumber(v)
            }
          />
          <Tooltip
            formatter={(value: number | string): string =>
              isCost
                ? `¥${formatMoney(value)}`
                : `${formatNumber(value)} tokens`
            }
          />
          <Line
            type="monotone"
            dataKey={dataKey}
            stroke="#4f46e5"
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}
